import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { ActiveEmployee } from '../../models/active-employee.model';
import { EmployeeView } from '../../models/employee-view.model';
import { TaskService } from '../task/task.service';
import { BaseTask } from '../../models/base-task.model';
import { ProblemInstanceService } from '../problem-instance/problem-instance.service';
import { EmployeeService } from '../employee/employee.service';

@Injectable()
export class ActiveEmployeeService {
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly taskService = inject(TaskService);
    private readonly employeeService = inject(EmployeeService);

    private readonly activeEmployee$: BehaviorSubject<ActiveEmployee>;

    constructor() {
        this.activeEmployee$ = new BehaviorSubject(null);
    }

    public getActiveEmployee(): Observable<ActiveEmployee> {
        return this.activeEmployee$.asObservable().pipe(filter(employee => employee != null));
    }

    public setActiveEmployee(employee: EmployeeView, reset?: boolean): void {
        if (!reset && this.activeEmployee$.value?.workHourId === employee.workHourId) {
            return;
        }

        this.taskService.getEmployeeTasks(employee.workHourId).subscribe(tasks => {
            const activeEmployee = <ActiveEmployee>{
                employeeId: employee.employeeId,
                workHourId: employee.workHourId,
                skillLevelId: employee.skillLevelId,
                fromTime: employee.fromTime,
                toTime: employee.toTime,
                isActive: employee.isActive,
                shiftCode: employee.shiftCode,
                tasks: [...tasks],
                visitRestrictions: null
            };

            this.activeEmployee$.next(activeEmployee);
        });
    }

    public updateActiveEmployeeInfo(): void {
        this.employeeService.getSelectedEmployee().subscribe(employee => {
            this.activeEmployee$.value.isActive = employee.isActive;
            this.activeEmployee$.value.fromTime = employee.fromTime;
            this.activeEmployee$.value.toTime = employee.toTime;
            this.activeEmployee$.value.shiftCode = employee.shiftCode;
            this.activeEmployee$.value.skillLevelId = employee.skillLevelId;
        });
    }

    public resetActiveEmployeeTasks(): void {
        if (this.activeEmployee$.value) {
            this.taskService.getEmployeeTasks(this.activeEmployee$.value.workHourId).subscribe(tasks => {
                this.activeEmployee$.value.tasks = [...tasks];
                this.activeEmployee$.next(this.activeEmployee$.value);
            });
        }
    }

    public selectActiveEmployee(employees: EmployeeView[], employeeWorkHourIdToSelect?: string): void {
        if (employeeWorkHourIdToSelect) {
            let employee = employees.find(i => i.selected);
            if (employee) {
                employee.selected = false;
            }

            employee = employees.find(i => i.workHourId === employeeWorkHourIdToSelect);
            employee.selected = true;
        }

        const selectedEmployee = employees?.find(employee => employee.selected);
        if (selectedEmployee) {
            this.setActiveEmployee(selectedEmployee, employeeWorkHourIdToSelect != null);
        }
    }

    public mapToActiveEmployee(employee: EmployeeView, routeTasks: BaseTask[]): ActiveEmployee {
        return <ActiveEmployee>{
            employeeId: employee.employeeId,
            workHourId: employee.workHourId,
            skillLevelId: employee.skillLevelId,
            fromTime: employee.fromTime,
            toTime: employee.toTime,
            isActive: employee.isActive,
            shiftCode: employee.shiftCode,
            tasks: routeTasks,
            visitRestrictions: null
        };
    }

    public mapToActiveEmployeeIncludeVisitRestrictions(
        employee: EmployeeView,
        routeTasks: BaseTask[]
    ): Observable<ActiveEmployee> {
        return this.problemInstanceService.getProblemInstance().pipe(
            map(result => {
                const activeEmployee = this.mapToActiveEmployee(employee, routeTasks);
                activeEmployee.visitRestrictions = result.problemInstance.visitRestrictions.filter(
                    i => i.employeeId === employee.employeeId
                );
                return activeEmployee;
            })
        );
    }
}
